<template>
  <header
    class="the-header"
    :class="[
      { 'supplier dark-theme': supplierStyle },
      { 'md:px-5': isSuppliersIndexPage },
      { transparent: transparentStyle },
      { 'temp-supplier-three-home': supplierThreeStyle },
      { 'temp-supplier-three': isSupplierThree },
      { 'temp-supplier-four-home': supplierFourStyle },
      { 'temp-supplier-four': isSupplierFour },
      { 'temp-supplier-four': isSupplierFive },
      { '!shadow-none !bg-transparent': supplierFiveStyle },
      { 'template4-home': template4Style },
      { 'template2-home': template2Style },
      { template4: isTemplate4 },
      { scrolled: isScrolled },
    ]"
    :style="{ backgroundColor: bgColor }"
  >
    <div class="container addition-layout-padding h-full">
      <div class="main-menu">
        <NuxtLink
          class="logo hidden lg:block"
          :class="
            headerData.logoHeightSize ? headerData.logoHeightSize : 'h-[36px]'
          "
          :to="localePath('/')"
        >
          <NuxtImg
            loading="lazy"
            v-no-right-click
            :src="
              (supplierStyle && headerData.logoDarkMode) ||
              (supplierThreeStyle && headerData.logoDarkMode) ||
              (supplierFourStyle && headerData.logoDarkMode) ||
              (template4Style && headerData.logoDarkMode) ||
              (transparentStyle && headerData.logoDarkMode)
                ? headerData.logoDarkMode
                : headerData.logo
            "
            alt="logo"
          />
        </NuxtLink>
        <NuxtLink
          class="logo inline-flex lg:hidden h-[52px]"
          :to="localePath('/')"
        >
          <NuxtImg
            loading="lazy"
            v-no-right-click
            :src="
              (template4Style && headerData.logoDarkMode) ||
              (transparentStyle && headerData.logoDarkMode) ||
              (supplierFourStyle && headerData.logoDarkMode)
                ? headerData.logoDarkMode
                : headerData.mobileLogo
            "
            alt="logo"
          />
        </NuxtLink>
        <SearchBoxTwo
          v-if="headerData.hasSearchBar"
          :top-cities="topCities!"
          :is-box="false"
          :just-icon="true"
          class="lg:!hidden w-4/6"
        />
        <Menu
          class="!hidden lg:!flex"
          :header-data="headerData"
          :currencies-list="currenciesList!"
          :top-cities="topCities!"
          :is-supplier="isSupplier"
          :route-path="routePath"
          :search-box-is-box="searchBoxIsBox"
          @open-login="emit('open-login', $event)"
        />
        <div class="flex lg:hidden">
          <Cart
            class="lg:hidden ms-4"
            @open-login="emit('open-login', $event)"
          />
          <button
            class="menu-button icon ms-4"
            @click="modalStatus = true"
            aria-label="toggle"
          >
            <icon-hamburger />
          </button>
          <BaseBottomSheet v-model:show="modalStatus" :full-height="true">
            <Menu
              class="mt-3"
              :header-data="headerData"
              :currencies-list="currenciesList ?? []"
              :top-cities="topCities ?? []"
              :is-supplier="isSupplier"
              :search-box-is-box="true"
              :route-path="routePath"
              @open-login="emit('open-login', $event)"
            />
          </BaseBottomSheet>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAsyncData, useRuntimeConfig } from "nuxt/app";
import type { ICurrency, ITopCities } from "../../types/general";
import Menu from "./TheHeaderMenu.vue";
import SearchBoxTwo from "../search-box-two/SearchBoxTwo.vue";
import customFetch from "../../composables/customFetch";

const localePath = useLocalePath();
const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const emit = defineEmits(["open-login"]);
const props = defineProps({
  backgroundTransparent: { type: Boolean, default: false },
  backgroundColor: { type: String, default: null }, // default color is gray-5
  routePath: { type: String, default: "/" },
  isSupplier: { type: Boolean, default: false },
  isSupplierThree: { type: Boolean, default: false },
  isSupplierFour: { type: Boolean, default: false },
  isSupplierFive: { type: Boolean, default: false },
  isTransparent: { type: Boolean, default: false },
  isTemplate4: { type: Boolean, default: false },
  isTemplate2: { type: Boolean, default: false },
});

const modalStatus = ref<boolean>(false);
const configData = ref<RuntimeConfiguration>(config);
const isScrolled = ref<boolean>(false);

const headerData = configData.value.public.header;
const heroConfig = config.public.supplierStatic?.hero || null;

const [{ data: currenciesList }, { data: topCities }] = await Promise.all([
  useAsyncData<ICurrency[]>("currencies", () => customFetch(`api/currencies`)),
  useAsyncData<ITopCities[]>("top-cities", () => customFetch(`api/top-cities`)),
]);

watch(
  () => props.routePath,
  () => {
    checkScroll();
    modalStatus.value = false;
  }
);

const searchBoxIsBox = computed(() => {
  return props.isSupplier
    ? false
    : props.routePath == localePath("/")
      ? isScrolled.value
      : true;
});

const supplierStyle = computed(() => {
  return isSuppliersIndexPage.value && !isScrolled.value;
});
const supplierThreeStyle = computed(() => {
  return isSupplierThreeIndexPage.value && !isScrolled.value;
});
const supplierFourStyle = computed(() => {
  return isSupplierFourIndexPage.value && !isScrolled.value;
});
const supplierFiveStyle = computed(() => {
  return isSupplierFiveIndexPage.value && !isScrolled.value;
});
const transparentStyle = computed(() => {
  return isTransparentIndexPage.value && !isScrolled.value;
});
const template4Style = computed(() => {
  return isTemplate4IndexPage.value && !isScrolled.value;
});
const template2Style = computed(() => {
  return isTemplate2IndexPage.value && !isScrolled.value;
});
const bgColor = computed(() => {
  return props.backgroundTransparent
    ? "transparent"
    : props.backgroundColor
      ? `var(--${props.backgroundColor})`
      : "";
});

const isSuppliersIndexPage = computed(() => {
  return props.isSupplier && props.routePath == localePath("/");
});
const isSupplierThreeIndexPage = computed(() => {
  return props.isSupplierThree && props.routePath == localePath("/");
});
const isSupplierFourIndexPage = computed(() => {
  return props.isSupplierFour && props.routePath == localePath("/");
});
const isSupplierFiveIndexPage = computed(() => {
  return props.isSupplierFive && props.routePath == localePath("/");
});
const isTransparentIndexPage = computed(() => {
  return props.isTransparent && props.routePath == localePath("/");
});
const isTemplate4IndexPage = computed(() => {
  return props.isTemplate4 && props.routePath == localePath("/");
});
const isTemplate2IndexPage = computed(() => {
  return props.isTemplate2 && props.routePath == localePath("/");
});

const checkScroll = () => {
  isScrolled.value = isSuppliersIndexPage.value
    ? document.body.scrollTop > 150
    : isTemplate4IndexPage.value
      ? document.body.scrollTop > 100
      : document.body.scrollTop > 20;

  // props.isSupplier ||
  // props.isSupplierThree ||
  // props.isSupplierFour ||
  // props.isSupplierFive
  //   ? false
  //   : props.isTemplate4
  //     ? document.body.scrollTop > (document.body.clientHeight * 45) / 100
  //     : document.body.scrollTop > (document.body.clientHeight * 90) / 100;
};

onMounted(() => {
  document.body.addEventListener("scroll", checkScroll);
});

onUnmounted(() => {
  document.body.removeEventListener("scroll", checkScroll);
});
</script>
